/* Central content source for the TRT landing page */

export const NAV_LINKS = [
  { label: "Chain", href: "#chain" },
  { label: "AI", href: "#ai" },
  { label: "RWA", href: "#rwa" },
  { label: "Ecosystem", href: "#ecosystem" },
  { label: "Token", href: "#token" },
  { label: "Staking", href: "#staking" },
  { label: "Nodes", href: "#nodes" },
  { label: "Roadmap", href: "#roadmap" },
];

export const HERO_STATS = [
  { label: "Total Value Locked", to: 0, prefix: "$" },
  { label: "Active Wallets", to: 0 },
  { label: "Validator Nodes", to: 0 },
  { label: "Properties Tokenized", to: 0 },
];

export const MARQUEE = [
  "EVM COMPATIBLE", "AI ANALYTICS", "RWA TOKENIZATION", "DPoS CONSENSUS",
  "ON-CHAIN STAKING", "NODE VALIDATORS", "DEX LIQUIDITY", "FRACTIONAL OWNERSHIP",
];

/* Sections 2-6: pillar features rendered by FeatureSection */
export const PILLARS = [
  {
    id: "chain",
    tag: "TRT Chain",
    title: "An EVM-compatible Layer-1",
    highlight: "built for real-world assets",
    desc: "A high-throughput, EVM-compatible blockchain secured by a Delegated Proof-of-Stake validator set. Deploy Solidity contracts, settle property transactions in seconds, and pay negligible fees.",
    points: [
      { k: "Consensus", v: "Delegated Proof-of-Stake" },
      { k: "Block Time", v: "~2.1 seconds" },
      { k: "Finality", v: "Instant, deterministic" },
      { k: "Compatibility", v: "Full EVM / Solidity" },
    ],
    accent: "gold",
    visual: "chain",
  },
  {
    id: "ai",
    tag: "TRT AI",
    title: "AI valuation & risk",
    highlight: "intelligence engine",
    desc: "On-chain analytics powered by machine learning. TRT AI scores properties, forecasts yield, detects fraud patterns and surfaces market insights directly inside the ecosystem.",
    points: [
      { k: "Property Scoring", v: "Automated AVM models" },
      { k: "Yield Forecasts", v: "Predictive analytics" },
      { k: "Fraud Detection", v: "Anomaly monitoring" },
      { k: "Market Signals", v: "Real-time dashboards" },
    ],
    accent: "cyan",
    visual: "ai",
  },
  {
    id: "rwa",
    tag: "TRT RWA",
    title: "Tokenized real-world",
    highlight: "assets, on-chain",
    desc: "Bring physical real-world assets on-chain as compliant, fractional tokens. Each asset is backed 1:1, legally structured, and tradable 24/7 with transparent ownership records.",
    points: [
      { k: "Ownership", v: "Fractional & liquid" },
      { k: "Backing", v: "1:1 asset-backed" },
      { k: "Compliance", v: "KYC / legal wrappers" },
      { k: "Settlement", v: "Instant on-chain" },
    ],
    accent: "emerald",
    visual: "rwa",
  },
  {
    id: "dapps",
    tag: "TRT DApps",
    title: "A growing suite of",
    highlight: "decentralized apps",
    desc: "A native application layer — marketplace, portfolio manager, rewards hub and governance — all interoperable and connected to your single TRT identity and wallet.",
    points: [
      { k: "Marketplace", v: "Buy & sell property tokens" },
      { k: "Portfolio", v: "Unified asset dashboard" },
      { k: "Governance", v: "On-chain voting" },
      { k: "Rewards Hub", v: "Claim & compound" },
    ],
    accent: "gold",
    visual: "dapps",
  },
  {
    id: "dex",
    tag: "TRT DEX",
    title: "Deep liquidity with",
    highlight: "the native DEX",
    desc: "Swap TRT and property tokens instantly through an automated market maker. Provide liquidity, earn trading fees, and access transparent, non-custodial pricing.",
    points: [
      { k: "Engine", v: "AMM liquidity pools" },
      { k: "Custody", v: "Fully non-custodial" },
      { k: "LP Rewards", v: "Share of trading fees" },
      { k: "Slippage", v: "Low, optimized routing" },
    ],
    accent: "cyan",
    visual: "dex",
  },
];

export const ECOSYSTEM_LAYERS = [
  { name: "TRT Chain", note: "EVM Layer-1 · DPoS", tone: "gold" },
  { name: "Smart Contracts", note: "Staking · Rewards · RWA · Nodes", tone: "cyan" },
  { name: "TRT AI Engine", note: "Valuation · Risk · Insights", tone: "emerald" },
  { name: "DApps & DEX", note: "Marketplace · Swaps · Governance", tone: "gold" },
  { name: "Investor Layer", note: "Wallets · Dashboard · Rewards", tone: "cyan" },
];

export const PROBLEMS = [
  { p: "Illiquid assets locked for years", s: "Fractional tokens tradable 24/7 on the DEX" },
  { p: "High entry cost & minimums", s: "Own property from a fraction of the value" },
  { p: "Opaque paperwork & middlemen", s: "Transparent, on-chain ownership records" },
  { p: "Slow, costly cross-border deals", s: "Instant settlement with negligible fees" },
  { p: "Guesswork on valuation", s: "AI-driven pricing & yield forecasting" },
  { p: "No passive yield on holdings", s: "Staking, rewards & node validator income" },
];

export const TECH_STACK = [
  "Solidity", "EVM", "Ethers.js", "Web3", "DPoS", "IPFS",
  "Machine Learning", "Chainlink Oracles", "The Graph", "MetaMask", "AMM DEX", "Merkle Proofs",
];

export const TOKENOMICS = [
  { name: "Staking Rewards", value: 30, color: "#f4c95d" },
  { name: "Ecosystem & RWA", value: 22, color: "#4fe0ff" },
  { name: "Node Validators", value: 16, color: "#37e0a6" },
  { name: "Team & Advisors", value: 12, color: "#4c7dff" },
  { name: "Liquidity & DEX", value: 10, color: "#e9b949" },
  { name: "Treasury", value: 10, color: "#8b93a7" },
];

export const TOKEN_FACTS = [
  { k: "Token", v: "TRT" },
  { k: "Standard", v: "Native + ERC-20" },
  { k: "Max Supply", v: "11,000,000" },
  { k: "Consensus", v: "DPoS" },
];

export const STAKING_TIERS = [
  { name: "Flexible", lock: "No lock", apy: 8, min: "100 TRT", featured: false },
  { name: "Growth", lock: "90 days", apy: 14, min: "1,000 TRT", featured: true },
  { name: "Validator", lock: "365 days", apy: 22, min: "25,000 TRT", featured: false },
];

export const BONUSES = [
  {
    id: "booster",
    title: "Booster Bonus",
    tag: "Accelerator",
    desc: "Boost your base staking APY with time-locked multipliers. The longer you commit, the higher your reward velocity.",
    metric: "up to 2.5x",
    metricLabel: "reward multiplier",
  },
  {
    id: "referral",
    title: "Direct Referral Bonus",
    tag: "Growth",
    desc: "Earn an instant on-chain bonus for every direct referral who stakes into the TRT ecosystem.",
    metric: "up to 10%",
    metricLabel: "direct commission",
  },
  {
    id: "team",
    title: "Team Staking Bonus",
    tag: "Network",
    desc: "Grow a multi-level team and earn from the collective staking volume across your network tree.",
    metric: "5 levels",
    metricLabel: "deep team rewards",
  },
  {
    id: "rewards",
    title: "Rewards Bonus",
    tag: "Loyalty",
    desc: "Milestone-based loyalty rewards distributed automatically as your holdings and activity grow.",
    metric: "auto-claim",
    metricLabel: "milestone payouts",
  },
];

export const NODE_TIERS = [
  { name: "Light Node", stake: "10,000 TRT", reward: "Base rewards", slots: "Unlimited" },
  { name: "Full Node", stake: "50,000 TRT", reward: "+ Fee share", slots: "5,000 slots" },
  { name: "Validator", stake: "250,000 TRT", reward: "+ Block rewards", slots: "1,240 slots" },
];

export const ROADMAP = [
  { q: "Phase 01", title: "Foundation", done: true, items: ["TRT Chain testnet", "Core smart contracts", "Wallet & dashboard MVP"] },
  { q: "Phase 02", title: "Tokenization", done: true, items: ["RWA framework live", "First property listings", "Staking & rewards launch"] },
  { q: "Phase 03", title: "Expansion", done: false, items: ["Native DEX & liquidity", "Node validator program", "AI valuation engine v1"] },
  { q: "Phase 04", title: "Scale", done: false, items: ["Mainnet launch", "Cross-chain bridges", "Global RWA marketplace"] },
];

export const FAQS = [
  { q: "What is TRT (The Real Token)?", a: "TRT is an AI-powered, EVM-compatible blockchain ecosystem for tokenized real-world assets. It combines a Layer-1 chain, RWA tokenization, AI analytics, a DEX, staking and validator nodes into one investor-grade platform." },
  { q: "How are assets tokenized on TRT?", a: "Each asset is legally structured and represented on-chain as fractional, asset-backed tokens. Ownership is transparent, tradable 24/7, and settled instantly through TRT smart contracts." },
  { q: "How does staking work?", a: "You lock TRT into on-chain staking contracts and earn rewards based on your chosen tier and lock period. Flexible, Growth and Validator tiers offer different APYs and multipliers." },
  { q: "What does TRT AI do?", a: "TRT AI provides automated property valuation, yield forecasting, fraud detection and real-time market signals — surfaced directly inside your dashboard to guide decisions." },
  { q: "Can I run a validator node?", a: "Yes. By staking the required amount you can operate a Light Node, Full Node or Validator and earn a share of block rewards and network fees through the DPoS model." },
  { q: "Is TRT non-custodial?", a: "Yes. You connect your own Web3 wallet (e.g. MetaMask). You always retain control of your keys and assets — TRT never holds your private keys." },
];

export const SOCIALS = ["Twitter", "Telegram", "Discord", "Medium", "GitHub", "LinkedIn"];
